// SEXTON'S SECOND TAP, for the setup surface's destructive acts — Freeze the roll,
// Open a ballot, and Abandon a draft. The same shape ScrutineerPage's own
// ConfirmBlock takes (a `<h2 tabIndex={-1}>` prompt that focus moves to, a Cancel
// listed FIRST so the confirming press cannot land where the primary button was),
// lifted into its own file here because three panels share it and a fourth copy is
// how "Cancel first" quietly becomes "Cancel second" on one of them.
//
// IT IS NOT REUSED FROM ScrutineerPage: that component keeps its ConfirmBlock
// private and this one carries two things it does not — an optional field group
// above the buttons (the abandon reason) and a `confirmDisabled` gate (so the
// confirm cannot fire until that field is valid). Importing across the two
// surfaces to share the buttons and then widening the shared thing for one caller
// is the churn that makes a shared component drift; a second, honest copy of a
// 40-line control is the smaller cost.

import { useRef, type JSX, type ReactNode } from 'react';
import { useFocusOnMount } from '../a11y/useFocusOnMount';

interface Props {
  /** The plain-words question. Focus moves here on mount so the destructive
   *  question is spoken and the keyboard lands inside the confirm. */
  prompt: string;
  /** The affirmative button's label — always specific ("Yes, freeze the roll"),
   *  never a bare "Confirm", so the press names what it does. */
  confirmLabel: string;
  busy: boolean;
  /** True while a required field above the buttons is not yet valid — the confirm
   *  is disabled, so the empty case never reaches the server's 422. */
  confirmDisabled?: boolean;
  /** An optional field group rendered ABOVE the two buttons (the abandon reason).
   *  Nothing is rendered where there is none. */
  children?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmBlock({
  prompt,
  confirmLabel,
  busy,
  confirmDisabled = false,
  children,
  onConfirm,
  onCancel,
}: Props): JSX.Element {
  const promptRef = useRef<HTMLHeadingElement>(null);
  useFocusOnMount(promptRef);

  return (
    <div className="setup-confirm" role="group" aria-label="Confirm this step">
      <h2 className="setup-confirm-prompt" tabIndex={-1} ref={promptRef}>
        {prompt}
      </h2>
      {children}
      {/* CANCEL FIRST. The confirming press must not land where the primary
          button was on the screen this confirm replaced. */}
      <div className="setup-confirm-actions">
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary setup-confirm-primary"
          onClick={onConfirm}
          disabled={busy || confirmDisabled}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </div>
  );
}
